// Stage default assignees, embedded in the Manage Team modal. One
// row per canonical stage from STAGES; each row carries a single
// dropdown that maps the stage to a team member (or unassigned).
// Saves via PUT /api/.../stage-defaults/:stageKey on change.
import { useEffect, useMemo, useState } from "react";
import type {
  StageDefaultDto,
  StageKey,
  TeamMemberDto,
} from "../../../../../../shared/types/production-schedule";
import { setStageDefault, useStageDefaults } from "../hooks/useProductionApi";
import { useActiveStages } from "../components/StagesProvider";

interface Props {
  team: TeamMemberDto[];
}

export function StageDefaultsSection({ team }: Props) {
  const { data, refetch, error } = useStageDefaults();
  const stages = useActiveStages();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => { void refetch(); }, [refetch]);

  const byKey = useMemo(() => {
    const m = new Map<string, StageDefaultDto>();
    for (const d of data?.defaults ?? []) m.set(d.stageKey, d);
    return m;
  }, [data]);

  const onChange = async (stageKey: StageKey, raw: string) => {
    setBusyKey(stageKey);
    setActionError(null);
    try {
      const id = raw ? Number(raw) : null;
      await setStageDefault(stageKey, id);
      await refetch();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div style={{ marginTop: 18 }}>
      <div style={{
        fontFamily: "var(--bsp-font-display)",
        fontSize: 11,
        fontWeight: 700,
        color: "var(--bsp-blue)",
        textTransform: "uppercase",
        letterSpacing: "0.14em",
        marginBottom: 8,
        paddingBottom: 8,
        borderBottom: "1px solid var(--bsp-border)",
      }}>
        Stage default assignees
      </div>
      <p style={{ fontSize: 11, color: "var(--bsp-fg-muted)", marginBottom: 10, lineHeight: 1.45 }}>
        Pick the team member who normally runs each stage. New spools pre-fill
        their Assigned to column from these defaults; per-spool overrides on
        the stage details panel still take precedence.
      </p>
      {(error ?? actionError) ? (
        <div style={{ color: "var(--bsp-red-fg)", fontSize: 12, marginBottom: 8 }}>
          {actionError ?? error}
        </div>
      ) : null}
      <table className="stages-table">
        <thead>
          <tr>
            <th style={{ width: 60 }}>Stage</th>
            <th>Name</th>
            <th>Default assignee</th>
          </tr>
        </thead>
        <tbody>
          {stages.map((def) => {
            const current = byKey.get(def.stageKey)?.defaultAssigneeId ?? null;
            return (
              <tr key={def.stageKey}>
                <td>
                  <span className="stage-tag" style={{ background: def.color }}>{def.short}</span>
                </td>
                <td style={{ fontSize: 12, fontWeight: 600, color: "var(--bsp-dark)" }}>
                  {def.name}
                </td>
                <td>
                  <select
                    className="stage-inp w-asg"
                    value={current ?? ""}
                    disabled={busyKey === def.stageKey}
                    onChange={(e) => void onChange(def.stageKey as StageKey, e.target.value)}
                    style={{ width: "100%" }}
                  >
                    <option value="">— Unassigned —</option>
                    {team.map((m) => (
                      <option key={m.id} value={m.id}>{m.initials} · {m.name}</option>
                    ))}
                  </select>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {team.length === 0 ? (
        <div style={{
          marginTop: 8,
          fontSize: 11,
          color: "var(--bsp-fg-muted)",
          fontStyle: "italic",
        }}>
          Add team members above before you can assign stage defaults.
        </div>
      ) : null}
    </div>
  );
}
